import React from "react";
import { Coins } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { StrategyRdHeadlineStats, StrategyRdSizing } from "../../../types/strategyRd";
import { formatStrategyRdPct } from "../../../utils/strategyRd";
import { PNL_NEGATIVE_COLOR, PNL_POSITIVE_COLOR } from "../Statistics/components/statsSignedPnlBarUtils";
import { strategyRdBrandActionButtonClassName } from "./strategyRdCardShell";
import { strategyRdSectionCardClassName } from "./strategyRdLabUi";

const signed = (v: number | null | undefined) => (v == null ? undefined : v >= 0 ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR);

function costText(c: Partial<StrategyRdSizing>): string {
  const parts: string[] = [];
  if (c.spread_pct) parts.push(`spread ${c.spread_pct}% of price`);
  if (c.swap_long_pct || c.swap_short_pct) {
    parts.push(`swap ${c.swap_long_pct ?? 0}% long / ${c.swap_short_pct ?? 0}% short per night`);
  }
  return parts.join(" · ");
}

/**
 * A backtest without trading costs, next to the same trades at the instrument's typical
 * spread and swap — both results at a glance, and one click to adopt the costs (they can then
 * be changed in Backtest settings).
 */
export function StrategyRdCostsCompare({
  withoutCosts,
  withCosts,
  onUseCosts,
}: {
  withoutCosts: StrategyRdHeadlineStats | null | undefined;
  withCosts: { costs: Partial<StrategyRdSizing>; stats: StrategyRdHeadlineStats } | null | undefined;
  onUseCosts?: (costs: Partial<StrategyRdSizing>) => void;
}): JSX.Element | null {
  if (!withCosts) return null;
  const before = withoutCosts?.return_on_starting_equity_pct ?? null;
  const after = withCosts.stats.return_on_starting_equity_pct ?? null;
  return (
    // Three rows: the title, what typical costs do to the return, then which costs (editable).
    <div className={cn(strategyRdSectionCardClassName, "gap-3")} data-strategy-rd-costs-compare="">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h3 className="m-0 font-[family-name:var(--font-primary)] text-base font-medium text-[color:var(--brand-tropical-surf)]">
          This backtest has no trading costs
        </h3>
        {onUseCosts ? (
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            data-font="primary"
            className={strategyRdBrandActionButtonClassName}
            onClick={() => onUseCosts(withCosts.costs)}
          >
            <Coins className="size-4" aria-hidden />
            <span className="normal-case tracking-normal">Use these costs</span>
          </Button>
        ) : null}
      </div>
      <p className="m-0 font-[family-name:var(--font-primary)] text-sm text-gray-500 dark:text-white">
        With typical costs the return goes{" "}
        <span className="font-ibm-plex font-medium tabular-nums" style={{ color: signed(before) }}>
          {formatStrategyRdPct(before) ?? "N/A"}
        </span>
        {" to "}
        <span className="font-ibm-plex font-semibold tabular-nums" style={{ color: signed(after) }}>
          {formatStrategyRdPct(after) ?? "N/A"}
        </span>
        {withCosts.stats.max_drawdown_pct != null ? (
          <> · worst drop {formatStrategyRdPct(withCosts.stats.max_drawdown_pct) ?? "N/A"}</>
        ) : null}
      </p>
      <p className="m-0 font-[family-name:var(--font-primary)] text-xs text-gray-500 dark:text-white">
        {costText(withCosts.costs)} · editable in Settings
      </p>
    </div>
  );
}
